import React, { RefObject, useEffect, useRef, useState } from 'react';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

import { User } from '@repo/domain/user';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { timeAgo } from '@repo/editor/src/utils/timeAgo';
import { withEventScope } from '@repo/event-processor';
import InfoThickIcon from '@repo/ui/components/Icon/InfoThickIcon';
import { ProfileImage } from '@repo/ui/deprecated/ProfileImage';
import { themeStyle } from '@repo/ui/theming';
import { buildProfileImageUrl } from '@repo/utils/url';

import Api from 'global/commentsApi';
import { CommentContent } from 'global/types/Comment';
import { buildPublicProfilePath } from 'global/urlUtils';

import ReplyList from '../ReplyList';

import { CommentItemMention } from './CommentItemMention';
import {
  AuthorName,
  CommentActions,
  CommentContainer,
  CommentContentWrapper,
  CommentDelete,
  CommentReply,
  CommentText,
  CommentTextWrapper,
  CommentWrapper,
  StyledComment,
  StyledDeletedMessage,
  TreeLine,
} from './styles';
import { CommentItemProps } from './types';

const DeletedMessage: React.FC = () => (
  <StyledDeletedMessage>
    <InfoThickIcon
      height="14px"
      theme={{
        color: themeStyle.varInkMedium,
        activeDefaultColor: themeStyle.varInkMedium,
        activeColor: themeStyle.varInkMedium,
        secondaryColor: themeStyle.varBackgroundAlt,
        secondaryActiveColor: themeStyle.varBackgroundBase,
        secondaryDisabledColor: themeStyle.varBackgroundAlt,
      }}
    />
    This message is deleted
  </StyledDeletedMessage>
);

const Content: React.FC<{
  contentItems: CommentContent[] | undefined;
  modalRef?: RefObject<HTMLDivElement>;
  getUserBySlug: (slug: string) => Promise<User | null>;
  closeParent?: () => void;
}> = ({ contentItems, modalRef, getUserBySlug, closeParent }) => {
  return (
    <>
      {contentItems?.map((contentItem, i) => (
        <span key={i}>
          {contentItem.mentionUserSlug ? (
            <CommentItemMention
              closeParent={closeParent}
              content={contentItem.text}
              getUserBySlug={getUserBySlug}
              wrapperRef={modalRef}
            />
          ) : (
            contentItem.text
          )}
        </span>
      ))}
    </>
  );
};

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  postId,
  onDelete,
  isReply,
  isFirst,
  closeParent,
  modalRef,
  getUserBySlug,
}) => {
  const user = useUserStore(userStoreSelector.user);
  const searchParams = useSearchParams();
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState(comment.replies);

  const toggleReplies = (): void => {
    setShowReplies(!showReplies);
  };

  const authorSlug = comment?.userProfile?.slug;
  const isCurrentPage = authorSlug === searchParams?.get('slug');

  const handleSamePageClick = (
    e: React.MouseEvent<HTMLAnchorElement>
  ): void => {
    if (!isCurrentPage) {
      return;
    }
    e.preventDefault();
    closeParent && closeParent();
  };

  const handleDelete = async (): Promise<void> => {
    const response = await Api.deleteComment({ commentId: comment.id });
    if (response.error) {
      return;
    }

    onDelete && onDelete(comment);
  };

  const handleNewReply = (): void => {
    setReplies(replies + 1);
  };

  const handleDeleteReply = (): void => {
    setReplies(replies - 1);
  };

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (comment.isNew) {
      containerRef.current?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [comment.isNew]);

  return (
    <StyledComment ref={containerRef} isReply={isReply}>
      {isReply && <TreeLine isFirst={isFirst} />}
      <CommentWrapper isReply={isReply}>
        <CommentContainer>
          <Link
            href={buildPublicProfilePath(authorSlug)}
            onClick={handleSamePageClick}
          >
            <ProfileImage
              profile={{
                name: comment.userProfile.name || '',
                profileImage: buildProfileImageUrl(
                  comment.userProfile.profileImage
                ),
                isTeam: comment.userProfile.isTeam,
              }}
              size="30px"
            />
          </Link>
          <CommentContentWrapper>
            <CommentTextWrapper>
              <CommentText>
                <Link
                  legacyBehavior
                  passHref
                  href={buildPublicProfilePath(authorSlug)}
                >
                  <AuthorName as="a" onClick={handleSamePageClick}>
                    {comment.userProfile.name}
                  </AuthorName>
                </Link>
                {!comment.deleted && (
                  <Content
                    closeParent={closeParent}
                    contentItems={comment.content}
                    getUserBySlug={getUserBySlug}
                    modalRef={modalRef}
                  />
                )}
                {comment.deleted && <DeletedMessage />}
              </CommentText>
            </CommentTextWrapper>
            <CommentActions>
              <div>
                {timeAgo(comment.createdAt, false, false)}
                {!isReply && (
                  <>
                    {'  '}·{'  '}
                    <CommentReply onClick={toggleReplies}>
                      {!!replies && (
                        <>
                          {replies} {replies === 1 ? 'reply' : 'replies'}
                        </>
                      )}
                      {!replies && <>Reply</>}
                    </CommentReply>
                  </>
                )}
              </div>
              {user &&
                comment.userProfile.slug &&
                comment.userProfile.slug === user.slug &&
                !comment.deleted && (
                  <CommentDelete onClick={handleDelete}>Delete</CommentDelete>
                )}
            </CommentActions>
          </CommentContentWrapper>
        </CommentContainer>
        {!isReply && showReplies && (
          <ReplyList
            commentId={comment.id}
            getUserBySlug={getUserBySlug}
            isDeleted={!!comment.deleted}
            modalRef={modalRef}
            postId={postId}
            onDeleteReply={handleDeleteReply}
            onNewReply={handleNewReply}
          />
        )}
      </CommentWrapper>
    </StyledComment>
  );
};

export default withEventScope(CommentItem, { module: 'commentItem' });
